/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Payment, PaymentRequest } from "./payment.type";
import { createStripeCheckout } from "../../../services/firebaseService";

export const PaymentApi = createApi({
  reducerPath: "paymentApi",
  tagTypes: ["payment", "payments"],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    initPaymentStripeCheckout: builder.mutation({
      queryFn: async (data: PaymentRequest) => {
        try {
          const response = await createStripeCheckout(data);
          return {
            data: {
              response,
              message:
                "Vous serez dirigé pour effectuer le paiement depuis stripe.",
            },
          };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["payment", "payments"],
    }),
    getPaymentBySessionId: builder.query({
      queryFn: async (sessionId) => {
        // Le queryFn initial récupère la donnée une première fois
        try {
          const paymentRef = doc(db, "payments", sessionId);
          const paymentSnap = await getDoc(paymentRef);

          if (!paymentSnap.exists()) {
            return { data: null }; // On retourne null car le webhook n'a peut-être pas encore écrit
          }

          return {
            data: {
              id: paymentSnap.id,
              ...paymentSnap.data(),
            } as unknown as Payment,
          };
        } catch (error: any) {
          return { error: { status: 500, message: error.message } };
        }
      },
      async onCacheEntryAdded(
        sessionId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe = () => { };

        try {
          // On attend que la première requête (queryFn) soit terminée
          await cacheDataLoaded;

          const paymentRef = doc(db, "payments", sessionId);

          // On s'abonne aux changements Firestore
          unsubscribe = onSnapshot(paymentRef, (snapshot) => {
            updateCachedData((draft) => {
              if (snapshot.exists()) {
                const data = {
                  id: snapshot.id,
                  ...snapshot.data(),
                } as unknown as Payment;
                if (draft) {
                  return Object.assign(draft, data);
                } else {
                  return data as any;
                }
              }
            });
          });
        } catch (error) {
          console.error("Erreur de synchronisation temps réel:", error);
        }

        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: ["payment"],
    }),
    getPaymentsByUser: builder.query({
      queryFn: async (userId) => {
        const paymentsRef = collection(db, "payments");
        const q = query(
          paymentsRef,
          where("userId", "==", userId),
          orderBy("date", "desc"),
        );

        const snapshot = await getDocs(q);

        const payments = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const payment = docSnap.data();

            return {
              id: docSnap.id,
              ...payment,
            };
          }),
        );

        return { data: payments as unknown as Payment[] };
      },
      // Gestion du temps réel avec Firestore et `onSnapshot`
      async onCacheEntryAdded(
        userId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe;

        try {
          await cacheDataLoaded;

          const paymentsRef = collection(db, "payments");

          const q = query(
            paymentsRef,
            where("userId", "==", userId),
            orderBy("date", "desc"),
          );

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const payments = await Promise.all(
              snapshot.docs.map(async (docSnap) => {
                const payment = docSnap.data();

                return {
                  id: docSnap.id,
                  ...payment,
                };
              }),
            );

            // Met à jour le cache RTK Query avec les nouvelles données
            updateCachedData(() => payments as unknown as Payment[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["payments"],
    }),
  }),
});

export const {
  useLazyGetPaymentBySessionIdQuery,
  useInitPaymentStripeCheckoutMutation,
  useGetPaymentsByUserQuery,
} = PaymentApi;
